// src/pages/nations/NationForm.tsx

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Upload, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { nationsService } from "@/api/services/nations.service";
import { toast } from "react-toastify";
import type { Nation, CreateNationRequest } from "@/types/api.types";

interface NationFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nation: Partial<Nation> | null;
  isEditing: boolean;
  onSave: (data: CreateNationRequest) => void;
  isSaving: boolean;
}

const GROUPS = [
  "Group A",
  "Group B",
  "Group C",
  "Group D",
  "Group E",
  "Group F",
  "Group G",
  "Group H",
  "Group I",
  "Group J",
  "Group K",
  "Group L",
];

export default function NationForm({
  open,
  onOpenChange,
  nation,
  isEditing,
  onSave,
  isSaving,
}: NationFormProps) {
  const [flagPreview, setFlagPreview] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CreateNationRequest>({
    defaultValues: {
      name: "",
      code: "",
      flagUrl: "",
      groupName: "",
    },
  });

  useEffect(() => {
    if (nation) {
      reset({
        name: nation.name || "",
        code: nation.code || "",
        flagUrl: nation.flagUrl || "",
        groupName: nation.groupName || "",
      });
      setFlagPreview(nation.flagUrl || "");
    }
  }, [nation, reset]);

  // Auto-generate code from name
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (!isEditing && name.length >= 3) {
      setValue("code", name.substring(0, 3).toUpperCase());
    }
  };

  // Handle flag upload
  const handleFlagUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size should be less than 2MB");
      return;
    }

    setIsUploading(true);

    try {
      const response = await nationsService.uploadFlag(file);
      setValue("flagUrl", response.url);
      setFlagPreview(response.url);
      toast.success("Flag uploaded successfully");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.message || "Failed to upload flag");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFlag = () => {
    setValue("flagUrl", "");
    setFlagPreview("");
  };

  const onSubmit = (data: CreateNationRequest) => {
    onSave(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-gray-900">
            {isEditing ? "Edit Nation" : "Add New Nation"}
          </DialogTitle>

          <DialogDescription>
            {isEditing
              ? "Update nation details, group and flag."
              : "Create a new nation for the tournament."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-gray-700">
              Nation Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              {...register("name", { required: "Nation name is required" })}
              onChange={handleNameChange}
              placeholder="Brazil"
              className="bg-white border-gray-300"
            />
            {errors.name && (
              <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Code */}
          <div>
            <Label htmlFor="code" className="text-gray-700">
              ISO Code <span className="text-red-500">*</span>
            </Label>
            <Input
              id="code"
              {...register("code", {
                required: "Code is required",
                minLength: { value: 3, message: "Code must be 3 characters" },
                maxLength: { value: 3, message: "Code must be 3 characters" },
                pattern: {
                  value: /^[A-Z]{3}$/,
                  message: "Code must be 3 uppercase letters",
                },
              })}
              placeholder="BRA"
              maxLength={3}
              className="bg-white border-gray-300 font-mono uppercase"
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase();
              }}
            />
            <p className="text-xs text-gray-500 mt-1">
              3-letter country code (auto-generated from name)
            </p>
            {errors.code && (
              <p className="text-sm text-red-500 mt-1">{errors.code.message}</p>
            )}
          </div>

          {/* Group */}
          <div>
            <Label htmlFor="groupName" className="text-gray-700 mb-2">
              World Cup Group
            </Label>
            <Select
              value={watch("groupName") || "none"}
              onValueChange={(value: string | undefined) =>
                setValue("groupName", value === "none" ? "" : value)
              }
            >
              <SelectTrigger className="bg-white border-gray-300">
                <SelectValue placeholder="Select group" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Group</SelectItem>
                {GROUPS.map((group) => (
                  <SelectItem key={group} value={group}>
                    {group}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              Assign to tournament group stage
            </p>
          </div>

          {/* Flag Upload */}
          <div>
            <Label htmlFor="flag" className="text-gray-700">
              Nation Flag
            </Label>

            {/* Preview */}
            {flagPreview && (
              <div className="mt-2 relative inline-block">
                <img
                  src={flagPreview}
                  alt="Flag preview"
                  className="w-32 h-20 object-cover border rounded-lg"
                />
                <button
                  type="button"
                  onClick={handleRemoveFlag}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Upload Button */}
            <div className="mt-2">
              <label
                htmlFor="flag-upload"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded-md cursor-pointer hover:bg-blue-100 transition"
              >
                <Upload className="h-4 w-4" />
                {isUploading ? "Uploading..." : "Upload Flag"}
              </label>
              <input
                id="flag-upload"
                type="file"
                accept="image/*"
                onChange={handleFlagUpload}
                disabled={isUploading}
                className="hidden"
              />
            </div>

            {/* Or URL Input */}
            <div className="mt-3">
              <Input
                {...register("flagUrl")}
                placeholder="Or paste flag image URL"
                type="url"
                className="bg-white border-gray-300"
                onChange={(e) => setFlagPreview(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Flag image (upload or paste URL)
              </p>
            </div>
          </div>

          <DialogFooter className="bg-white pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving || isUploading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || isUploading}>
              {isSaving ? "Saving..." : isEditing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
